import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5fccb2af"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "p-recommend-form__modal-card -is-calendar" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "p-recommend-form__text-6" }, "卒業年早見表", -1))
const _hoisted_3 = { class: "p-recommend-form__graduation-calendar-wrap" }
const _hoisted_4 = { class: "p-recommend-form__graduation-calendar js-graduation-calendar" }
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", null, "生まれ年"),
    /*#__PURE__*/_createElementVNode("th", null, "中学卒業"),
    /*#__PURE__*/_createElementVNode("th", null, "高校卒業"),
    /*#__PURE__*/_createElementVNode("th", null, "大学卒業")
  ])
], -1))
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = ["data-year", "innerHTML", "onClick"]
const _hoisted_8 = ["data-year", "innerHTML", "onClick"]
const _hoisted_9 = ["data-year", "innerHTML", "onClick"]

import { computed, watch, onBeforeUnmount } from 'vue';

// Props to control modal visibility from parent component
interface JapaneseEra {
  name: string;      // Era name in Japanese (e.g., 令和, 平成)
  startYear: number; // Western year when era started
  endYear: number;   // Western year when era ended
}

// Definition of Japanese eras with their respective time periods
interface GraduationYear {
  western: number;                    // Year in western calendar
  japanese: { era: string; year: number }; // Year in Japanese era format
}

// Interface for each row in the graduation table
interface GraduationRow {
  birthYear: {
    western: number;
    japanese: { era: string; year: number };
  };
  middleSchool: GraduationYear;  // Middle school graduation (typically age 15)
  highSchool: GraduationYear;    // High school graduation (typically age 18)
  university: GraduationYear;    // University graduation (typically age 22)
}

/**
 * Computed property that generates graduation year data
 * Creates a table showing birth years and corresponding graduation years
 * for middle school, high school, and university
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'QuickViewModal',
  props: {
    isOpen: { type: Boolean }
  },
  emits: ["update:isOpen", "yearSelected"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:isOpen', value: boolean): void;
  (e: 'yearSelected', year: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  isOpen: boolean
}



// Emit events to update parent component about modal state
// Added yearSelected event to pass the selected year to parent


// Function to close the modal and notify parent component
const close = () => {
  emit('update:isOpen', false);
  document.body.style.overflow = '';
};

/**
 * Function to handle year selection from table cells
 * Emits the selected year to parent component and closes the modal
 * @param year - The western year that was selected
 */
const selectYear = (year: number) => {
  emit('yearSelected', year);
  close(); // Close the modal after selection
};

// Interface for Japanese era periods
const japaneseEras: JapaneseEra[] = [
  { name: '令和', startYear: 2019, endYear: 2100 }, // Reiwa (2019-present)
  { name: '平成', startYear: 1989, endYear: 2019 }, // Heisei (1989-2019)
  { name: '昭和', startYear: 1926, endYear: 1989 }, // Showa (1926-1989)
  { name: '大正', startYear: 1912, endYear: 1926 }, // Taisho (1912-1926)
];

/**
 * Converts a western year to Japanese era format
 * @param westernYear - Year in western calendar
 * @returns Object containing era name and year number within that era
 */
const convertToJapaneseEra = (westernYear: number): { era: string; year: number } => {
  for (const era of japaneseEras) {
    if (westernYear >= era.startYear && westernYear < era.endYear) {
      const eraYear = westernYear - era.startYear + 1;
      return { era: era.name, year: eraYear };
    }
  }
  return { era: '', year: 0 };
};

// Interface for graduation year data structure
const graduationYears = computed(() => {
  const years: GraduationRow[] = [];
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 18;
  // Generate data from 2025 to 1925 (newest to oldest)
  for (let birthYear = startYear; birthYear >= currentYear - 100; birthYear--) {
    const japaneseYear = convertToJapaneseEra(birthYear);
    
    // Calculate graduation years based on typical Japanese education system
    const middleSchoolYear = birthYear + 15 + 1;
    const highSchoolYear = birthYear + 18 + 1;
    const universityYear = birthYear + 22 + 1;
    
    years.push({
      birthYear: {
        western: birthYear,
        japanese: japaneseYear
      },
      middleSchool: {
        western: middleSchoolYear,
        japanese: convertToJapaneseEra(middleSchoolYear)
      },
      highSchool: {
        western: highSchoolYear,
        japanese: convertToJapaneseEra(highSchoolYear)
      },
      university: {
        western: universityYear,
        japanese: convertToJapaneseEra(universityYear)
      }
    });
  }
  
  return years;
});

/**
 * Formats birth year for display
 * Handles special case for first year of era (元年)
 * @param birthYear - Birth year object with western and Japanese formats
 * @returns Formatted HTML string for display
 */
const formatBirthYear = (birthYear: { western: number; japanese: { era: string; year: number } }): string => {
  const japaneseYearText = birthYear.japanese.year === 1 
    ? `${birthYear.japanese.era}元年` // First year of era is displayed as 元年
    : `${birthYear.japanese.era}${birthYear.japanese.year}年`;
  
  return `${japaneseYearText}<br>（${birthYear.western}年）`;
};

/**
 * Formats graduation year for display
 * Handles special case for first year of era (元年)
 * @param graduationYear - Graduation year object with western and Japanese formats
 * @returns Formatted HTML string for display
 */
const formatGraduationYear = (graduationYear: GraduationYear): string => {
  const japaneseYearText = graduationYear.japanese.year === 1 
    ? `${graduationYear.japanese.era}元年` // First year of era is displayed as 元年
    : `${graduationYear.japanese.era}${graduationYear.japanese.year}年`;
  
  return `${japaneseYearText}<br>（${graduationYear.western}年）`;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' || event.key === 'Esc') {
    close();
  }
};

// Watch for changes to isOpen prop to add/remove event listener
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    // Add event listener when modal opens
    document.addEventListener('keydown', handleKeyDown);
  } else {
    // Remove event listener when modal closes
    document.removeEventListener('keydown', handleKeyDown);
  }
});

// Clean up event listener when component is unmounted
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["p-recommend-form__modal", { 'is-active': __props.isOpen }]),
    id: "graduation-calendar"
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("button", {
        class: "p-recommend-form__modal-help-close",
        type: "button",
        onClick: close
      }, "閉じる"),
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("table", _hoisted_4, [
          _hoisted_5,
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(graduationYears), (year, index) => {
              return (_openBlock(), _createElementBlock("tr", { key: index }, [
                _createElementVNode("th", {
                  innerHTML: formatBirthYear(year.birthYear)
                }, null, 8, _hoisted_6),
                _createElementVNode("td", {
                  "data-year": year.middleSchool.western,
                  innerHTML: formatGraduationYear(year.middleSchool),
                  onClick: ($event: any) => (selectYear(year.middleSchool.western)),
                  class: "clickable-cell"
                }, null, 8, _hoisted_7),
                _createElementVNode("td", {
                  "data-year": year.highSchool.western,
                  innerHTML: formatGraduationYear(year.highSchool),
                  onClick: ($event: any) => (selectYear(year.highSchool.western)),
                  class: "clickable-cell"
                }, null, 8, _hoisted_8),
                _createElementVNode("td", {
                  "data-year": year.university.western,
                  innerHTML: formatGraduationYear(year.university),
                  onClick: ($event: any) => (selectYear(year.university.western)),
                  class: "clickable-cell"
                }, null, 8, _hoisted_9)
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    _createElementVNode("div", {
      class: "p-recommend-form__modal-close-area",
      onClick: close
    })
  ], 2))
}
}

})